import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  ChevronDown,
  MapPin,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneOff,
  Play,
  PlayCircle,
  SkipForward,
  Voicemail,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useCallStore } from '@/stores/useCallStore'
import { useCallActions } from '@/hooks/useCallActions'
import {
  resolveCallerId,
  useExecuteVmDrop,
  useLogProspectCall,
  useVmDrops,
} from '@/api/powerDialer'
import type { PowerDialerCampaign, PowerDialerProspect } from '@/types/powerDialer'

interface Props {
  campaignId: number
  campaign: PowerDialerCampaign
  activeProspect: PowerDialerProspect | null
  selectedNumber: string
  onSelectNumber: (n: string) => void
  onBack: () => void
  onSkip: () => void
  onOpenVmManager: () => void
  darkMode: boolean
}

function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

function formatCallDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

// Extrait l'area code NANP (3 chiffres après +1) d'un numéro normalisé.
// "+15145551234" → "514". Retourne null si format non NANP.
function extractAreaCode(phone: string | null | undefined): string | null {
  if (!phone) return null
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11 && cleaned.startsWith('1')) return cleaned.slice(1, 4)
  if (cleaned.length === 10) return cleaned.slice(0, 3)
  return null
}

// Libellé humain de la raison pour laquelle le système a choisi ce numéro,
// affiché à l'agent pendant l'appel (source renvoyée par le backend).
type TFn = (key: string, defaultValue: string) => string
function callerIdSourceLabel(source: string | null, t: TFn): string {
  switch (source) {
    case 'manual':
    case 'agent_fallback':
      return t('powerDialer.session.srcChosen', 'Numéro choisi')
    case 'pinned':
      return t('powerDialer.session.srcPinned', 'Numéro figé')
    case 'sticky':
      return t('powerDialer.session.srcSticky', 'Numéro habituel')
    case 'local_presence':
      return t('powerDialer.session.srcLocalArea', 'Présence locale (indicatif)')
    case 'local_presence_region':
      return t('powerDialer.session.srcLocalRegion', 'Présence locale (région)')
    case 'local_presence_nearby':
      return t('powerDialer.session.srcLocalNearby', 'Présence locale (proche)')
    case 'rotation':
      return t('powerDialer.session.srcRotation', 'Rotation')
    case 'campaign_default':
      return t('powerDialer.session.srcDefault', 'Numéro par défaut')
    case 'client_fallback':
      return t('powerDialer.session.srcOffline', 'Numéro (hors-ligne)')
    default:
      return t('powerDialer.session.srcShown', 'Numéro présenté')
  }
}

export function PdCallBar({
  campaignId,
  campaign,
  activeProspect,
  selectedNumber,
  onSelectNumber,
  onBack,
  onSkip,
  onOpenVmManager,
  darkMode,
}: Props) {
  const { t } = useTranslation()
  // État appel global Twilio (partagé avec PhoneTab/SMSTab/etc.)
  const isCallConnected = useCallStore((s) => s.isCallConnected)
  const isCallMuted = useCallStore((s) => s.isCallMuted)
  const callDuration = useCallStore((s) => s.callDuration)
  const isOnHold = useCallStore((s) => s.isOnHold)
  const currentCall = useCallStore((s) => s.currentCall)

  const { makeOutboundCall, toggleMute, hangUp, hold, unhold } = useCallActions()

  const [showVmMenu, setShowVmMenu] = useState(false)
  const vmMenuRef = useRef<HTMLDivElement>(null)
  // Mode caller ID (design Figma) : Automatique = décision serveur complète ;
  // Manuel = l'agent choisit librement parmi TOUS les numéros de la campagne
  // (le choix est mémorisé pour ce contact dans cette campagne, côté serveur).
  const [callerIdMode, setCallerIdMode] = useState<'auto' | 'manual'>('auto')
  // Numéro réellement présenté par le système pour l'appel en cours + raison
  // (renvoyés par le backend au moment de composer). Affiché à l'agent.
  const [resolvedCall, setResolvedCall] = useState<{
    callerId: string
    source: string
  } | null>(null)

  // Efface le numéro présenté quand on passe à un autre prospect.
  useEffect(() => {
    setResolvedCall(null)
  }, [activeProspect?.id])

  const { data: vmDrops = [] } = useVmDrops()
  const executeVmDrop = useExecuteVmDrop()
  const logCall = useLogProspectCall(
    campaignId,
    activeProspect ? activeProspect.id : 0,
  )

  // Fallback CLIENT (mode dégradé seulement) : match d'indicatif local. La
  // décision nominale est prise côté SERVEUR via resolveCallerId() au moment
  // de composer (sticky, cap quotidien et rotation vivent en DB — un calcul
  // client ne peut pas les garantir). Ce memo ne sert que si l'endpoint est
  // injoignable, pour ne jamais bloquer un appel.
  const localPresenceMatch = useMemo<string | null>(() => {
    if (!campaign.local_presence_enabled) return null
    const prospectArea = extractAreaCode(activeProspect?.phone)
    if (!prospectArea) return null
    const numbers = campaign.campaign_numbers ?? []
    for (const cn of numbers) {
      const num = cn.phone_number_details.phone_number
      if (extractAreaCode(num) === prospectArea) return num
    }
    return null
  }, [
    campaign.local_presence_enabled,
    campaign.campaign_numbers,
    activeProspect?.phone,
  ])

  const fallbackCallerId = localPresenceMatch || selectedNumber

  // Fermer dropdown VM au clic extérieur
  useEffect(() => {
    if (!showVmMenu) return
    const handler = (e: MouseEvent) => {
      if (vmMenuRef.current && !vmMenuRef.current.contains(e.target as Node)) {
        setShowVmMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showVmMenu])

  // Log call_sid au prospect actif dès que l'appel se connecte
  useEffect(() => {
    if (!isCallConnected || !activeProspect || !currentCall) return
    const sid = currentCall.id || ''
    if (!sid) return
    logCall.mutate(sid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCallConnected, activeProspect?.id])

  const border = darkMode ? 'border-slate-800' : 'border-slate-200'
  const cardBg = darkMode ? 'bg-slate-900' : 'bg-white'
  const cardBgSubtle = darkMode ? 'bg-slate-900/50' : 'bg-slate-50'
  const textPrimary = darkMode ? 'text-white' : 'text-slate-900'
  const textMuted = darkMode ? 'text-slate-500' : 'text-slate-400'
  const textFaint = darkMode ? 'text-slate-600' : 'text-slate-300'
  const hoverBg = darkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-100'

  // Badge du numéro RÉELLEMENT présenté par le serveur pour l'appel en cours
  // (source de vérité). Affiché en mode Auto ET Manuel : en manuel, le serveur
  // peut écarter l'override (cap/indisponible) — l'agent doit voir le numéro
  // effectivement composé, pas seulement son choix.
  const resolvedBadge = resolvedCall ? (
    <div
      className={cn(
        'flex items-center gap-2.5 px-3 py-1.5 rounded-[10px] border',
        darkMode ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200',
      )}
      title={`${t('powerDialer.session.shownTitle', "Présenté à l'appelé")} : ${formatPhone(resolvedCall.callerId)} - ${callerIdSourceLabel(resolvedCall.source, t)}`}
    >
      <span
        className={cn(
          'w-[7px] h-[7px] rounded-full shrink-0',
          resolvedCall.source.startsWith('local_presence')
            ? 'bg-emerald-400'
            : 'bg-slate-400',
        )}
      />
      <div className="min-w-0">
        <p className={cn('text-xs font-semibold truncate', textPrimary)}>
          {t('powerDialer.session.shown', 'Présenté')} ·{' '}
          {formatPhone(resolvedCall.callerId)}
        </p>
        <p
          className={cn(
            'text-[10px] font-medium truncate',
            resolvedCall.source.startsWith('local_presence')
              ? 'text-emerald-400'
              : textMuted,
          )}
        >
          {callerIdSourceLabel(resolvedCall.source, t)}
        </p>
      </div>
    </div>
  ) : null

  const handleCall = async () => {
    if (!activeProspect) return
    // Le caller ID est décidé côté SERVEUR au moment de composer (hiérarchie
    // pin > sticky > présence locale > rotation > fallback agent), avec
    // mémorisation du sticky et incrément du cap quotidien. En cas d'erreur
    // réseau on retombe sur le calcul client (fallbackCallerId) : un souci
    // d'endpoint ne doit jamais empêcher d'appeler.
    let callerId = fallbackCallerId
    try {
      const resolved = await resolveCallerId(
        campaignId,
        activeProspect.phone,
        selectedNumber || undefined,
        // Manuel : le numéro du sélecteur devient un override souverain.
        callerIdMode === 'manual' ? selectedNumber || undefined : undefined,
      )
      callerId = resolved.caller_id
      setResolvedCall({ callerId: resolved.caller_id, source: resolved.source })
    } catch {
      // mode dégradé : match local client ou numéro de session
      setResolvedCall(
        callerId ? { callerId, source: 'client_fallback' } : null,
      )
    }
    // usePdDevice: les calls PD doivent passer par le Device subaccount, sinon
    // Twilio rejette le Dial (le caller_id appartient au subaccount, pas au
    // master account du Device principal).
    void makeOutboundCall(
      activeProspect.phone,
      callerId || undefined,
      undefined,
      { usePdDevice: true },
    )
  }

  const handleVmDropClick = (dropId: number) => {
    setShowVmMenu(false)
    if (!currentCall || !isCallConnected) return
    const sid = currentCall.id || ''
    if (!sid) return
    void executeVmDrop.mutateAsync({ call_sid: sid, drop_id: dropId }).then(() => {
      hangUp()
    })
  }

  return (
    <div className={cn('border-t shrink-0', border, cardBgSubtle)}>
      <div className="h-14 flex items-center justify-between px-5">
        {/* Gauche (design Figma) : Retour + contexte campagne + sélecteur de mode */}
        <div className="flex items-center gap-3.5 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-[10px] border text-[13px] font-semibold transition-colors',
              darkMode
                ? 'bg-slate-800/70 border-white/10 text-slate-300 hover:text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900',
            )}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('powerDialer.session.back', 'Retour')}
          </button>
          <div className="min-w-0">
            <p className={cn('text-[13px] font-semibold truncate', textPrimary)}>
              {campaign.name}
            </p>
            <p className={cn('text-[10px] font-medium', textMuted)}>
              {t('powerDialer.session.activeCampaign', 'Campagne active')}
            </p>
          </div>

          {/* Segment Automatique / Manuel */}
          <div
            className={cn(
              'flex items-center p-[3px] rounded-[10px] border',
              darkMode ? 'bg-slate-900 border-white/10' : 'bg-slate-100 border-slate-200',
            )}
          >
            {(
              [
                { value: 'auto', label: t('powerDialer.session.auto', 'Automatique') },
                { value: 'manual', label: t('powerDialer.session.manual', 'Manuel') },
              ] as const
            ).map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setCallerIdMode(m.value)}
                className={cn(
                  'px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors',
                  callerIdMode === m.value
                    ? 'bg-[#F5A623] text-[#1A2233]'
                    : darkMode
                      ? 'text-slate-400 hover:text-slate-300'
                      : 'text-slate-500 hover:text-slate-700',
                )}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Auto : badge "Présenté" (source serveur) · Manuel : choix libre
              + badge du numéro réellement composé (peut différer de l'override). */}
          {callerIdMode === 'manual' ? (
            <div className="flex items-center gap-2.5">
              <select
                value={selectedNumber}
                onChange={(e) => onSelectNumber(e.target.value)}
                className={cn(
                  'border text-xs font-semibold rounded-[10px] px-3 py-2 outline-none focus:ring-1 focus:ring-[#F5A623]',
                  cardBg,
                  darkMode ? 'border-[#F5A623]/50' : 'border-[#F5A623]/60',
                  textPrimary,
                )}
                title={t('powerDialer.session.manualSelectTitle', 'Numéro à présenter (mémorisé pour ce contact dans cette campagne)')}
              >
                {campaign.campaign_numbers?.map((cn) => (
                  <option
                    key={cn.phone_number_details.phone_number}
                    value={cn.phone_number_details.phone_number}
                  >
                    {cn.phone_number_details.label ||
                      formatPhone(cn.phone_number_details.phone_number)}
                  </option>
                ))}
              </select>
              {resolvedBadge}
            </div>
          ) : resolvedCall ? (
            resolvedBadge
          ) : localPresenceMatch ? (
            <div
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-[10px] border',
                darkMode ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200',
              )}
              title={`${t('powerDialer.session.localPresenceProbable', 'Présence locale probable')} : ${formatPhone(localPresenceMatch)}`}
            >
              <MapPin className="w-3 h-3 text-emerald-400" />
              <span className={cn('text-xs font-semibold', textPrimary)}>
                {formatPhone(localPresenceMatch)}
              </span>
            </div>
          ) : (
            <span className={cn('text-[10px] uppercase tracking-widest', textFaint)}>
              {t('powerDialer.session.decidedAtDial', "Décidé à l'appel")}
            </span>
          )}
        </div>

        {/* Droite : contrôles d'appel + VM Drop + Passer + Appeler */}
        <div className="flex items-center gap-2">
          {isCallConnected && (
            <>
              <div
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg',
                  darkMode ? 'bg-green-500/10' : 'bg-green-50',
                )}
              >
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span
                  className={cn(
                    'text-sm font-mono font-bold',
                    darkMode ? 'text-green-400' : 'text-green-600',
                  )}
                >
                  {formatCallDuration(callDuration)}
                </span>
              </div>
              <button
                type="button"
                onClick={toggleMute}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors',
                  isCallMuted
                    ? 'bg-red-500/10 border-red-500/30 text-red-400'
                    : darkMode
                      ? 'bg-slate-500/10 border-slate-500/20 text-slate-400 hover:bg-slate-500/20'
                      : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100',
                )}
              >
                {isCallMuted ? (
                  <MicOff className="w-3.5 h-3.5" />
                ) : (
                  <Mic className="w-3.5 h-3.5" />
                )}
                {isCallMuted ? t('powerDialer.session.muted', 'Sourdine') : t('powerDialer.session.mute', 'Muet')}
              </button>
              <button
                type="button"
                onClick={() => (isOnHold ? unhold() : hold())}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors',
                  isOnHold
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                    : darkMode
                      ? 'bg-slate-500/10 border-slate-500/20 text-slate-400 hover:bg-slate-500/20'
                      : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100',
                )}
              >
                {isOnHold ? (
                  <Play className="w-3.5 h-3.5" />
                ) : (
                  <Pause className="w-3.5 h-3.5" />
                )}
                {isOnHold ? t('powerDialer.session.resume', 'Reprendre') : t('powerDialer.session.hold', 'Attente')}
              </button>
              <button
                type="button"
                onClick={hangUp}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-bold transition-all"
              >
                <PhoneOff className="w-3.5 h-3.5" />
                {t('powerDialer.session.hangUp', 'Raccrocher')}
              </button>
              <div
                className={cn(
                  'w-px h-5',
                  darkMode ? 'bg-slate-600' : 'bg-slate-200',
                )}
              />
            </>
          )}

          {/* VM Drop dropdown */}
          <div className="relative" ref={vmMenuRef}>
            <button
              type="button"
              onClick={() => setShowVmMenu((v) => !v)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors',
                darkMode
                  ? 'bg-purple-500/10 border-purple-500/20 text-purple-400 hover:bg-purple-500/20'
                  : 'bg-purple-50 border-purple-200 text-purple-600 hover:bg-purple-100',
              )}
            >
              <Voicemail className="w-3.5 h-3.5" />
              {t('powerDialer.session.vmDrop', 'VM Drop')}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {showVmMenu && (
              <div
                className={cn(
                  'absolute bottom-full right-0 mb-2 w-64 rounded-xl border shadow-xl z-50',
                  darkMode
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-slate-200',
                )}
              >
                <div
                  className={cn(
                    'px-3 py-2 border-b flex items-center justify-between',
                    border,
                  )}
                >
                  <span
                    className={cn(
                      'text-xs font-semibold uppercase',
                      textMuted,
                    )}
                  >
                    {t('powerDialer.session.vmMyMessages', 'Mes messages vocaux')}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowVmMenu(false)
                      onOpenVmManager()
                    }}
                    className={cn(
                      'text-xs',
                      darkMode
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-blue-600 hover:text-blue-500',
                    )}
                  >
                    {t('powerDialer.session.vmManage', 'Gérer')}
                  </button>
                </div>
                {vmDrops.length === 0 ? (
                  <div className={cn('px-3 py-4 text-center', textMuted)}>
                    <Voicemail className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-xs">
                      {t('powerDialer.session.vmEmpty', 'Aucun message vocal')}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowVmMenu(false)
                        onOpenVmManager()
                      }}
                      className={cn(
                        'text-xs mt-2 font-medium',
                        darkMode ? 'text-purple-400' : 'text-purple-600',
                      )}
                    >
                      {t('powerDialer.session.vmRecordNew', '+ Enregistrer un message')}
                    </button>
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto">
                    {vmDrops.map((vm) => (
                      <button
                        key={vm.id}
                        type="button"
                        disabled={!isCallConnected}
                        onClick={() => handleVmDropClick(vm.id)}
                        className={cn(
                          'w-full text-left px-3 py-2 flex items-center gap-2 text-xs transition-colors',
                          isCallConnected
                            ? hoverBg
                            : 'opacity-40 cursor-not-allowed',
                        )}
                      >
                        <PlayCircle
                          className={cn(
                            'w-4 h-4 flex-shrink-0',
                            darkMode ? 'text-purple-400' : 'text-purple-600',
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <div
                            className={cn('font-medium truncate', textPrimary)}
                          >
                            {vm.name}
                          </div>
                          <div className={textMuted}>
                            {Math.round(vm.duration)}s
                          </div>
                        </div>
                        {!isCallConnected && (
                          <span
                            className={cn('text-[10px] flex-shrink-0', textMuted)}
                          >
                            {t('powerDialer.session.vmInCall', 'En appel')}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onSkip}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors',
              darkMode
                ? 'bg-slate-500/10 border-slate-500/20 text-slate-400 hover:bg-slate-500/20'
                : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100',
            )}
          >
            <SkipForward className="w-3.5 h-3.5" />
            {t('powerDialer.session.skip', 'Passer')}
          </button>

          {!isCallConnected && (
            <button
              type="button"
              onClick={() => void handleCall()}
              disabled={!activeProspect}
              className="flex items-center gap-1.5 px-5 py-1.5 bg-[#F5A623] hover:bg-[#e0951f] disabled:opacity-40 text-[#1A2233] rounded-lg text-xs font-bold transition-all shadow-lg shadow-[#F5A623]/20"
            >
              <Phone className="w-3.5 h-3.5" />
              {t('powerDialer.session.callBtn', 'Appeler')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
